import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/table-column.default.js';
import * as wireframeSkin from './skins/table-column.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

const VALID_SORT_DIRS = ['asc', 'desc'];
const VALID_ALIGNS = ['start', 'center', 'end'];

class RunTableColumn extends RunElement {
  #skin = null;
  #key = undefined;
  #sortable = false;
  #sortDirection = undefined;
  #width = undefined;
  #minWidth = undefined;
  #align = 'start';
  #bordered = false;
  #rendering = false;

  /** @type {import('../table/run-table.js').RunTable|null} */
  _parentTable = null;

  _boundHandleClick = this._handleClick.bind(this);
  _boundHandleKeydown = this._handleKeydown.bind(this);

  static get observedAttributes() {
    return ['skin', 'key', 'sortable', 'sort-direction', 'width', 'min-width', 'align', 'bordered'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this._boundHandleClick);
    this.addEventListener('keydown', this._boundHandleKeydown);
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this._boundHandleClick);
    this.removeEventListener('keydown', this._boundHandleKeydown);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'skin':
        this.#skin = newValue;
        this.requestUpdate();
        break;
      case 'key':
        this.#key = newValue ?? undefined;
        this.requestUpdate();
        break;
      case 'sortable':
        this.#sortable = newValue !== null;
        this.requestUpdate();
        break;
      case 'sort-direction': {
        const coerced = VALID_SORT_DIRS.includes(newValue) ? newValue : undefined;
        this.#sortDirection = coerced;
        this.requestUpdate();
        break;
      }
      case 'width':
        this.#width = newValue ?? undefined;
        this.requestUpdate();
        break;
      case 'min-width':
        this.#minWidth = newValue ?? undefined;
        this.requestUpdate();
        break;
      case 'align': {
        const coerced = VALID_ALIGNS.includes(newValue) ? newValue : 'start';
        this.#align = coerced;
        this.requestUpdate();
        break;
      }
      case 'bordered':
        this.#bordered = newValue !== null;
        this.requestUpdate();
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // skin
  // ---------------------------------------------------------------------------

  get skin() { return this.#skin; }

  set skin(value) {
    const coerced = value == null ? null : String(value);
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced === null) this.removeAttribute('skin');
    else this.setAttribute('skin', coerced);
    this.requestUpdate();
  }

  // ---------------------------------------------------------------------------
  // key
  // ---------------------------------------------------------------------------

  get key() { return this.#key; }

  set key(value) {
    const coerced = value == null ? undefined : String(value);
    if (this.#key === coerced) return;
    this.#key = coerced;
    if (coerced === undefined) this.removeAttribute('key');
    else this.setAttribute('key', coerced);
    this.requestUpdate();
  }

  // ---------------------------------------------------------------------------
  // sortable
  // ---------------------------------------------------------------------------

  get sortable() { return this.#sortable; }

  set sortable(value) {
    const coerced = Boolean(value);
    if (this.#sortable === coerced) return;
    this.#sortable = coerced;
    this.toggleAttribute('sortable', coerced);
    this.requestUpdate();
  }

  // ---------------------------------------------------------------------------
  // sortDirection
  // ---------------------------------------------------------------------------

  get sortDirection() { return this.#sortDirection; }

  set sortDirection(value) {
    const coerced = VALID_SORT_DIRS.includes(value) ? value : undefined;
    if (this.#sortDirection === coerced) return;
    this.#sortDirection = coerced;
    if (coerced === undefined) this.removeAttribute('sort-direction');
    else this.setAttribute('sort-direction', coerced);
    this.requestUpdate();
  }

  // ---------------------------------------------------------------------------
  // width
  // ---------------------------------------------------------------------------

  get width() { return this.#width; }

  set width(value) {
    const coerced = value == null ? undefined : String(value);
    if (this.#width === coerced) return;
    this.#width = coerced;
    if (coerced === undefined) this.removeAttribute('width');
    else this.setAttribute('width', coerced);
    this.requestUpdate();
  }

  // ---------------------------------------------------------------------------
  // minWidth
  // ---------------------------------------------------------------------------

  get minWidth() { return this.#minWidth; }

  set minWidth(value) {
    const coerced = value == null ? undefined : String(value);
    if (this.#minWidth === coerced) return;
    this.#minWidth = coerced;
    if (coerced === undefined) this.removeAttribute('min-width');
    else this.setAttribute('min-width', coerced);
    this.requestUpdate();
  }

  // ---------------------------------------------------------------------------
  // align
  // ---------------------------------------------------------------------------

  get align() { return this.#align; }

  set align(value) {
    const coerced = VALID_ALIGNS.includes(value) ? value : 'start';
    if (this.#align === coerced) return;
    this.#align = coerced;
    this.setAttribute('align', coerced);
    this.requestUpdate();
  }

  // ---------------------------------------------------------------------------
  // bordered
  // ---------------------------------------------------------------------------

  get bordered() { return this.#bordered; }

  set bordered(value) {
    const coerced = Boolean(value);
    if (this.#bordered === coerced) return;
    this.#bordered = coerced;
    this.toggleAttribute('bordered', coerced);
    this.requestUpdate();
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Sort interaction
  // ---------------------------------------------------------------------------

  _handleClick() {
    if (!this.#sortable) return;
    this._cycleSort();
  }

  _handleKeydown(e) {
    if (e.key === 'Enter' && this.#sortable) {
      this._cycleSort();
    }
  }

  _cycleSort() {
    const current = this.#sortDirection;
    let next;
    if (!current) next = 'asc';
    else if (current === 'asc') next = 'desc';
    else next = undefined;
    this.sortDirection = next;
    if (this._parentTable != null && typeof this._parentTable._handleColumnSort === 'function') {
      this._parentTable._handleColumnSort(this, next);
    }
  }

  // ---------------------------------------------------------------------------
  // Skin resolution
  // ---------------------------------------------------------------------------

  #resolveSkin() {
    if (this.#skin) return this.#skin;
    return resolveSkinFromAncestors(this);
  }
}

if (!customElements.get('run-table-column')) {
  customElements.define('run-table-column', RunTableColumn);
}

export { RunTableColumn };
export default RunTableColumn;
